import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { capitalize } from "lodash";
import dayjs from "dayjs";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { Stack, Tabs, TabsContent, TabsList, TabsTrigger } from "@altimateai/lego";
import { formatNumber } from "@/uiCore";
import { copyToClipboard } from "@/services/browser";
import { COPY_INTERACTION_TIME } from "@/helpers/constants";
import { RelativeComponentLoader } from "@/components/loader";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { ArrowLeftIcon } from "@/assets/icons";
import { useAppState } from "@/modules/app/useAppContext";
import Activity from "@/modules/insights/components/Activity";
import { useOpportunityDetailQuery } from "../hooks";
import { getEffortLabel, getOpportunityStatus, getResourceType } from "../utils";
import { DetailKpiStrip, DetailKpiItem } from "./DetailKpiStrip";
import { DetailCard } from "./DetailCard";
import { OpportunityInformation } from "./OpportunityInformation";
import { AgentInstructions } from "./AgentInstructions";

const fmtMoney = (n: number, currency: string) => {
  const fractions = n < 1 && n > 0 ? 2 : 0;
  return formatNumber(String(n), {
    currency,
    minimumFractionDigits: fractions,
    maximumFractionDigits: fractions,
  });
};

// Effort severity colours (foreground) for the KPI strip value, mirroring
// the EffortTag palette.
const EFFORT_FG: Record<string, string> = {
  low: "#067647",
  medium: "#B54708",
  high: "#B42318",
  zero: "#067647",
};

// Opportunity detail page, rebuilt to match the "Discover (A)" design
// (detail.jsx): breadcrumb + back, header (neutral resource chip + type +
// instance, then title), a divided KPI strip, then Information / Activity
// tabs. All values come from the existing detail query — no new
// data/behaviour. The design's header quick-action buttons are omitted
// (status/owner are already mutated via the Details sidebar).
export const OpportunityDetailPage = () => {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currency } = useAppState();

  const instance_id = useMemo(() => {
    const raw = searchParams.get("instance_id");
    return raw == null ? NaN : Number(raw);
  }, [searchParams]);

  const tab = searchParams.get("detail_tab") || "information";

  const { data, isLoading, error } = useOpportunityDetailQuery({
    opportunity_id: opportunityId || "",
    instance_id,
    enabled: Boolean(opportunityId) && Number.isFinite(instance_id),
  });

  // Surface this opportunity in the top breadcrumb bar (Discover / <name>)
  // rather than a duplicate in-page breadcrumb.
  useBreadcrumb(data?.opportunity_name ?? "Opportunity", {
    defaultParents: [{ label: "Discover", pathname: "/discover" }],
  });

  const [assignedStatus, setAssignedStatus] = useState("");
  const [assignedOwner, setAssignedOwner] = useState("");
  const [instructionsCopied, setInstructionsCopied] = useState(false);

  useEffect(() => {
    if (data) {
      // STATUS_OPTIONS uses lowercase string values ("active",
      // "implemented", "dismissed") — map the integer status from the
      // backend through `getOpportunityStatus` so the dropdown matches
      // its options and renders the selected value.
      setAssignedStatus(data.status != null ? getOpportunityStatus(data.status) : "active");
      setAssignedOwner(data.owner?.toString() ?? "");
    }
  }, [data]);

  const handleBack = () => navigate(-1);
  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("detail_tab", value);
    setSearchParams(next, { replace: true });
  };

  const backButton = (
    <button
      onClick={handleBack}
      aria-label="Back"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: 8,
        border: "1px solid #E4E7EC",
        background: "#fff",
        color: "#475467",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
      }}
    >
      <ArrowLeftIcon width={18} height={18} style={{ display: "block" }} />
    </button>
  );

  if (!opportunityId || !Number.isFinite(instance_id)) {
    return (
      <Stack direction="column" gap="md" align="center" className="al-w-full al-py-12">
        <span style={{ fontSize: 14, color: "#475467" }}>
          Invalid opportunity link — missing or malformed parameters.
        </span>
        {backButton}
      </Stack>
    );
  }
  if (isLoading) return <RelativeComponentLoader label="Loading opportunity" />;
  if (error || !data) {
    return (
      <Stack direction="column" gap="md" align="center" className="al-w-full al-py-12">
        <span style={{ fontSize: 14, color: "#475467" }}>Opportunity not found.</span>
        {backButton}
      </Stack>
    );
  }

  const effortLabel = getEffortLabel(data.effort);
  const resourceTypeLabel = getResourceType(data.resource_type);
  const agentInstructions = data.metadata?.agent_instructions?.trim() || "";
  const firstSeen = data.first_seen ? dayjs(data.first_seen).fromNow() : "—";
  const lastSeen = data.last_seen ? dayjs(data.last_seen).fromNow() : "—";
  const detections = data.count != null ? data.count.toLocaleString() : "—";
  const minNum = Number(data.money_savings?.min ?? 0);
  const maxNum = Number(data.money_savings?.max ?? 0);
  const hasSavings = !(minNum === 0 && maxNum === 0);

  const kpiItems: DetailKpiItem[] = [
    {
      label: "Potential savings / mo",
      value: hasSavings ? fmtMoney(maxNum, currency) : "N/A",
      sub: hasSavings ? `${fmtMoney(minNum, currency)} – ${fmtMoney(maxNum, currency)}` : undefined,
      highlight: true,
    },
    { label: "Time savings", value: data.time_savings || "—", sub: "per month, est." },
    { label: "Effort", value: capitalize(effortLabel), color: EFFORT_FG[effortLabel] },
    {
      label: "Detections",
      value: detections,
      sub: data.total_count ? `of ${data.total_count.toLocaleString()} runs` : "in period",
    },
    { label: "First detected", value: firstSeen },
    { label: "Last detected", value: lastSeen },
  ];

  return (
    <div style={{ padding: "24px 32px 40px", maxWidth: 1380, margin: "0 auto" }}>
      {/* header (back button sits inline just before the title; the
          breadcrumb trail lives in the top breadcrumb bar) */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 8,
            flexWrap: "wrap",
          }}
        >
          {resourceTypeLabel && (
            <span
              style={{
                fontSize: 11,
                color: "#667085",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 600,
              }}
            >
              {resourceTypeLabel}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {backButton}
          <h1
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 600,
              color: "#101828",
              letterSpacing: "-0.02em",
            }}
          >
            {data.opportunity_name}
          </h1>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <DetailKpiStrip items={kpiItems} />
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} variant="underlined">
        <TabsList>
          <TabsTrigger value="information">Information</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          {/* The copy affordance lives inside the tab label itself (copy only
              applies to agent instructions). It's a span, not a button, to
              avoid nesting an interactive button inside the tab trigger button;
              onPointerDown is stopped so copying doesn't also switch tabs. With
              no instructions there's simply nothing to copy. */}
          <TabsTrigger value="instructions">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Agent Instructions
              <span
                role="button"
                tabIndex={0}
                aria-label="Copy agent instructions"
                title="Copy agent instructions"
                style={{ display: "inline-flex", cursor: "pointer", color: "inherit" }}
                onPointerDown={e => e.stopPropagation()}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  copyToClipboard(agentInstructions);
                  setInstructionsCopied(true);
                  window.setTimeout(() => setInstructionsCopied(false), COPY_INTERACTION_TIME);
                }}
                onKeyDown={e => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  e.stopPropagation();
                  copyToClipboard(agentInstructions);
                  setInstructionsCopied(true);
                  window.setTimeout(() => setInstructionsCopied(false), COPY_INTERACTION_TIME);
                }}
              >
                {instructionsCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </span>
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="information" className="al-mt-3">
          <OpportunityInformation
            data={data}
            assignedStatus={assignedStatus}
            setAssignedStatus={setAssignedStatus}
            assignedOwner={assignedOwner}
            setAssignedOwner={setAssignedOwner}
          />
        </TabsContent>

        <TabsContent value="activity" className="al-mt-3">
          <DetailCard title="Activity">
            <Activity opportunity_id={data.opportunity_id} />
          </DetailCard>
        </TabsContent>

        <TabsContent value="instructions" className="al-mt-3">
          <AgentInstructions data={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
